// Balises SEO de la page (titre, meta, canonical, Open Graph, JSON-LD).
//
// ⚠️ Historique : ce composant utilisait react-helmet-async, INCOMPATIBLE avec React 19
// (il déclare react ^16|^17|^18 en peerDependency). Symptôme : le <head> n'était écrit qu'une
// fois puis restait figé — titre de la page précédente conservé en navigation SPA, et JSON-LD
// bloqué sur le 1er rendu (donc sans les données asynchrones : /temoignages perdait son
// aggregateRating, cf. US 6.16). On s'appuie désormais sur le hoisting natif de React 19 :
// <title>/<meta>/<link> rendus ici sont automatiquement remontés dans le <head>.
//
// ⚠️ React 19 ne déduplique PAS les <title>/<meta> : une seule source par route doit les rendre.
// SEOManager s'efface donc sur les routes dont la page rend son propre <SEO> (cf. SELF_MANAGED_ROUTES)
// et n'y fournit plus que le JSON-LD — d'où le `title` optionnel ci-dessous.
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOProps {
    /** Optionnel : sans titre, le composant ne rend QUE le JSON-LD (usage SEOManager). */
    title?: string;
    description?: string;
    imageUrl?: string;
    url?: string;
    type?: 'website' | 'article';
    noindex?: boolean;
    structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_DOMAIN = "https://www.stellarskyimager.fr";
const SITE_TITLE = "StellarSkyImager";

/** Tronque un paragraphe pour en faire une meta description exploitable (~160 caractères). */
export const metaFrom = (text: string, max = 160) => {
    if (text.length <= max) return text;
    const cut = text.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

// Schema de base — Organisation + WebSite (toutes les pages)
const BASE_SCHEMA = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Organization",
            "@id": `${SITE_DOMAIN}/#organization`,
            "name": "StellarSkyImager",
            "url": SITE_DOMAIN,
            "logo": `${SITE_DOMAIN}/favicon.png`,
            "description": "Free astrophotography planning platform: session planner, horizon generator, deep sky gallery.",
            "sameAs": [
                "https://youtube.com/@StellarSkyImager",
                "https://play.google.com/store/apps/details?id=fr.stellarskyimager.planner"
            ]
        },
        {
            "@type": "WebSite",
            "@id": `${SITE_DOMAIN}/#website`,
            "name": "StellarSkyImager",
            "url": SITE_DOMAIN,
            "inLanguage": ["fr", "en", "es", "de", "it", "zh"],
            "publisher": { "@id": `${SITE_DOMAIN}/#organization` }
        }
    ]
};

/**
 * Injecte un <script type="application/ld+json"> dans le <head> et le maintient à jour.
 * React 19 ne hoiste pas les scripts inline : on gère donc le nœud à la main.
 * Chaque instance possède SON nœud (plusieurs <SEO> coexistent : SEOManager + page).
 */
const useJsonLd = (json: string) => {
    const scriptRef = React.useRef<HTMLScriptElement | null>(null);

    React.useEffect(() => {
        if (!json) {
            scriptRef.current?.remove();
            scriptRef.current = null;
            return;
        }
        if (!scriptRef.current) {
            scriptRef.current = document.createElement('script');
            scriptRef.current.type = 'application/ld+json';
            document.head.appendChild(scriptRef.current);
        }
        scriptRef.current.textContent = json;
    }, [json]);

    // Nettoyage au démontage : évite qu'un schema survive à la navigation SPA.
    React.useEffect(() => () => { scriptRef.current?.remove(); scriptRef.current = null; }, []);
};

export const SEO: React.FC<SEOProps> = ({
    title,
    description,
    imageUrl = "/og-image.png",
    url,
    type = 'website',
    noindex = false,
    structuredData
}) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    // Génère une URL canonique propre : domaine fixe + pathname sans query params
    // Supprime le trailing slash sauf pour la homepage "/"
    const canonicalUrl = useMemo(() => {
        if (url) {
            return url.startsWith('http') ? url : `${SITE_DOMAIN}${url}`;
        }
        let pathname = location.pathname;
        if (pathname !== '/' && pathname.endsWith('/')) {
            pathname = pathname.slice(0, -1);
        }
        return `${SITE_DOMAIN}${pathname}`;
    }, [url, location.pathname]);

    // Si imageUrl est relative, on la rend absolue
    const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${SITE_DOMAIN}${imageUrl}`;
    // Pas de marque en double quand le titre la porte déjà (« Agenda Astronomique – StellarSkyImager »).
    // Même règle dans scripts/prerenderSeo.mjs (resolveRouteMeta).
    const fullTitle = !title ? '' : title.includes(SITE_TITLE) ? title : `${title} | ${SITE_TITLE}`;

    const pageJson = useMemo(
        () => (structuredData
            ? JSON.stringify({
                "@context": "https://schema.org",
                ...(Array.isArray(structuredData) ? { "@graph": structuredData } : structuredData)
            })
            : ''),
        [structuredData]
    );
    useJsonLd(pageJson);

    // Le schema de base n'est utile qu'une fois par page : porté par l'instance qui rend le titre.
    useJsonLd(title ? JSON.stringify(BASE_SCHEMA) : '');

    // <html lang> : hors périmètre du hoisting React, mis à jour à la main.
    React.useEffect(() => {
        if (title) document.documentElement.lang = i18n.language;
    }, [i18n.language, title]);

    // Sans titre, cette instance ne sert qu'à porter le JSON-LD (cf. en-tête de fichier).
    if (!title) return null;

    return (
        <>
            {/* Standard */}
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            <link rel="canonical" href={canonicalUrl} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}

            {/* Facebook / Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            {description && <meta property="og:description" content={description} />}
            <meta property="og:image" content={fullImageUrl} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            {description && <meta name="twitter:description" content={description} />}
            <meta name="twitter:image" content={fullImageUrl} />
        </>
    );
};
